import React from 'react';
import {MutableRefObject, useEffect, useRef, useState} from 'react';
import {DropdownV2, Icon, PriorityIcon, Separator} from '@priority-software/priority-style-react';
import {common, priority} from '@priority-software/priority-proto-api/lib';
import texts from '../../texts';
import {Item, ItemWithCheckbox} from './EditDropdownItem';
import styles from '../styles/editDropdown.module.scss';
import {formsStore, Record} from '../../../../../../stores/Form';
import {FileFields as RegularFileFields, MailboxSendFields} from '../consts';
import {getFieldValueByName} from '../../Common/utils';
import {RenameDialog} from './RenameDialog';
import {fieldValue} from '../../../../../../stores/Form/Field';
import {showConfirmMessage} from '../../../../../../_shared/Messages/actions/MessageActions';
import {downloadFile, parsePathToFileName, previewFile} from '../../../../utils/file-processing';
import usePortal from 'react-useportal';
import classNames from 'classnames';
import {onSaveChanges} from '../../Common/actions';
import {getUsername} from '../../../../../../_shared/GWTBridge/invoke/genPage';
import {handleScreenMessage} from '../../../../actions/screen';
interface EditDropdownProps {
    file: Record;
    isNoSend: boolean;
    isReadOnly: boolean;
    isDataExchange: boolean;
    containerRef: MutableRefObject<HTMLDivElement>;
    loadFileData: (Record) => Promise<common.IConstant>;
    setParentNeedsRefresh: (value: boolean) => void;
}

export const EditDropdown = ({
    isNoSend,
    isReadOnly,
    isDataExchange,
    file,
    containerRef,
    loadFileData,
    setParentNeedsRefresh
}: EditDropdownProps) => {
    const parentForm = file.parentForm;
    const FileFields =
        parentForm?.parentForm?.ename === 'MAILBOXSEND' ? MailboxSendFields : RegularFileFields;
    const {Portal} = usePortal();
    const triggerRef = useRef<HTMLDivElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);
    const containerRect = useRef(null);
    const elementRect = useRef(null);
    const [isEditDropOpen, setIsEditDropOpen] = useState(false);
    const [isRenaming, setIsRenaming] = useState(false);

    useEffect(() => {
        const checkForClickOutSide = e => {
            if (
                !isEditDropOpen ||
                menuRef.current?.contains(e.target) ||
                triggerRef.current?.contains(e.target)
            ) {
                return;
            }
            setIsEditDropOpen(false);
        };
        document.addEventListener('click', checkForClickOutSide);
        document.addEventListener('wheel', () => setIsEditDropOpen(false));
        return () => {
            document.removeEventListener('click', checkForClickOutSide);
            document.removeEventListener('wheel', () => setIsEditDropOpen(false));
        };
    }, [isEditDropOpen]);

    const setClientRects = () => {
        containerRect.current = containerRef.current.getBoundingClientRect();
        elementRect.current = triggerRef.current.getBoundingClientRect();
    };

    const isUpword = (): boolean => {
        const editDropdownHeight = 300;
        return containerRect.current?.bottom - elementRect.current?.bottom < editDropdownHeight;
    };

    const calculateTopOffset = (): number => {
        const triggerMargin = 10;
        return elementRect.current?.top - triggerMargin;
    };

    const onSave = async (fieldName: string, value: fieldValue) => {
        const lockerName = getFieldValueByName(file, FileFields.lockUser);

        if (lockerName !== getUsername() && isReadOnly) {
            const msgProps = {
                Messages: [
                    {
                        MType: priority.netitems.shared.Message.Type.Error,
                        Text: (texts.cantChangeErrorMsg as string)?.replace(
                            'X',
                            lockerName as string
                        ),
                        Links: {}
                    }
                ]
            };
            handleScreenMessage(msgProps, {});
        } else {
            setIsEditDropOpen(false);
            onSaveChanges(fieldName, file, value);
        }
    };

    const onDropdownOpen = () => {
        formsStore.currentScreen.getActiveForm()?.setCurrentRow(file.recordIndex);
    };

    const onPreview = async () => {
        setIsEditDropOpen(false);
        const fileData = await loadFileData(file);
        const fileName = getFieldValueByName(file, FileFields.fileName);
        previewFile(fileData, fileName.toString());
    };

    const onDownload = async () => {
        setIsEditDropOpen(false);
        const fileData = await loadFileData(file);
        const fileName = getFieldValueByName(file, FileFields.fileName);
        downloadFile(
            fileData,
            FileFields.fileName === FileFields.fileData
                ? parsePathToFileName(fileName.toString())
                : fileName.toString()
        );
    };

    const onDelete = async () => {
        setIsEditDropOpen(false);
        showConfirmMessage({
            onConfirm: () => deleteHandler(file),
            title: texts.delete_question,
            content: texts.delete_confirmation,
            confirmButtonText: texts.delete_yes,
            cancleButtonText: texts.cancel,
            defaultButton: 0
        });
    };

    const deleteHandler = file => {
        const isSuccess = formsStore.currentScreen.recordDeleteAction(file);
        if (isSuccess) {
            setParentNeedsRefresh(true);
        }
    };

    const handleOpen = () => {
        setClientRects();
        setIsEditDropOpen(true);
        onDropdownOpen();
    };

    return (
        <>
            <div ref={triggerRef}>
                <Icon
                    icon={PriorityIcon.threeDots}
                    componentClasses={styles.moreIcon}
                    onClick={handleOpen}
                    active={isEditDropOpen}
                    skin="primary"
                />
            </div>
            {isEditDropOpen && (
                <Portal>
                    <div className={styles.dropdown} style={{top: calculateTopOffset()}}>
                        <DropdownV2
                            openingSide={'leftSide'}
                            width={'contentWidth'}
                            isOpen
                            forceUpward={isUpword()}
                            contentComponentClasses={classNames(styles.menu, {
                                [styles.upwordMenu]: isUpword()
                            })}
                        >
                            <div ref={menuRef}>
                                <Item index={0} title={texts.preview} onClick={onPreview} />
                                <Item index={1} title={texts.download} onClick={onDownload} />
                                {FileFields.fileName !== FileFields.fileData && (
                                    <Item
                                        index={2}
                                        title={texts.rename}
                                        onClick={() => setIsRenaming(true)}
                                    />
                                )}
                                {!isReadOnly && (
                                    <Item
                                        index={3}
                                        title={texts.delete}
                                        onClick={onDelete}
                                        textColor={'validation-red'}
                                    />
                                )}
                                <Separator align="vertical" componentClasses={styles.separator} />
                                {FileFields.status && (
                                    <ItemWithCheckbox
                                        index={4}
                                        title={texts.label_readonly}
                                        checked={isReadOnly}
                                        onClick={checked => onSave(FileFields.status, checked)}
                                    />
                                )}
                                {FileFields.noSend && (
                                    <ItemWithCheckbox
                                        index={5}
                                        title={texts.label_not_for_sending}
                                        checked={isNoSend}
                                        onClick={checked => onSave(FileFields.noSend, checked)}
                                    />
                                )}
                                {FileFields.dataExchange &&
                                file.parentForm.getColumnByName(FileFields.dataExchange) ? (
                                    <ItemWithCheckbox
                                        index={6}
                                        title={texts.dataexchange}
                                        checked={isDataExchange}
                                        onClick={checked =>
                                            onSave(FileFields.dataExchange, checked)
                                        }
                                    />
                                ) : null}
                            </div>
                        </DropdownV2>
                    </div>
                </Portal>
            )}

            {isRenaming && (
                <RenameDialog onClose={() => setIsRenaming(false)} file={file} onSave={onSave} />
            )}
        </>
    );
};
